import React, {
  useMemo,
} from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  Controller,
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  BillingProviderDTO,
  BillingProviderRuleDTO,
  RenderingProviderDTO,
  ServiceFacilityDTO,
} from 'dtos';
import {
  useGetBillingProvider,
  useGetRenderingProvider,
  useUpdateBillingProviderRule,
} from 'views/EMR/Settings/hooks';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Payer,
} from 'pages/Dashboard/types/insurance';
import {
  BillingProviderInfo,
} from 'views/EMR/Settings/BillingConfigurations/types';
import InsuranceLookup from 'pages/Dashboard/components/InsuranceLookup';
import Select from 'core/components/Select';
import {
  BillingProviderLabel,
  UserLabel,
} from 'pages/Dashboard/pages/Billing/ClaimForm/Info';
import ServiceFacilitySelect from 'pages/Dashboard/components/ServiceFacilitySelect';

export type BillingProviderRuleDialogProps = {
  billingProviderRule?: BillingProviderRuleDTO | null;
  defaultOrder?: number;
};

type Props = BillingProviderRuleDialogProps & {
  onClose: () => void;
};

export type BillingProviderRuleForm = {
  payer?: Payer;
  renderingProvider?: RenderingProviderDTO;
  serviceFacility?: ServiceFacilityDTO;
  billingProvider: BillingProviderInfo;
  billingProviderRuleId?: number;
  organizationId?: number;
  order?: number;
};

function getDefaultValues(
  {
    order,
    billingProviderRuleId,
    serviceFacilityId: _serviceFacilityId,
    renderingProviderId: _renderingProviderId,
    billingProviderId,
    payerEntrySummary,
  }: BillingProviderRuleDTO,
  defaultOrder?: number,
) {
  const serviceFacilityId = isNil(_serviceFacilityId) ? 0 : _serviceFacilityId;
  const renderingProviderId = isNil(_renderingProviderId) ? 0 : _renderingProviderId;
  return {
    order: order ?? defaultOrder,
    billingProviderRuleId,
    serviceFacility: { serviceFacilityId },
    renderingProvider: { renderingProviderId },
    billingProvider: isNil(billingProviderId) ? null : { billingProviderId },
    payer: { payerName: payerEntrySummary?.name ?? '' },
  };
}

const defaultRenderingProvider = {
  renderingProviderId: 0,
  formattedNames: { indexByLastNameWithCredentials: '<Match Any>' },
  npi: { number: '' },
};

export default function BillingProviderRuleDialog({
  onClose,
  billingProviderRule,
  defaultOrder,
}: Props) {
  const isNew = isNil(billingProviderRule);

  const defaultValues = getDefaultValues(billingProviderRule ?? {}, defaultOrder);

  const formProps = useForm<BillingProviderRuleForm>({
    mode: 'onChange',
    defaultValues: defaultValues as BillingProviderRuleForm,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const { mutateAsync: upsertBillingProviderRule } = useUpdateBillingProviderRule();
  const { data: renderingProviders } = useGetRenderingProvider();
  const { data: billingProviders } = useGetBillingProvider();

  const renderingProvidersWithNotSet = useMemo(() => (
    [defaultRenderingProvider, ...(renderingProviders ?? [])]
  ), [renderingProviders]);

  const notify = useNotify();
  const userInfo = useUserInfo();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: BillingProviderRuleForm) => {
    try {
      await upsertBillingProviderRule({
        ...data,
        organizationId: userInfo?.organization?.organizationId,
      });
      notify({
        variant: 'success',
        message: `Billing Provider Rule ${isNew ? 'created' : 'updated'} successfully`,
      });
      settingsEventChannel.emit('onBillingProviderRuleChange');
      handleClose();
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title={(
          <div className="flex flex-1 flex-col gap-4">
            <h2 className="text-lg font-semibold">
              {`${isNew ? 'Add' : 'Edit'} Billing Provider Rule `}
            </h2>
          </div>
        )}
        closeBtnClasses="!-top-1"
        onClose={onClose}
        onConfirm={noop}
        className="!w-140 populate-form billing-rules-form"
        open
      >
        <div className="flex flex-col gap-form-row">
          <div className="flex flex-col gap-form-row">
            <Controller
              name="payer"
              control={control}
              render={({ field, fieldState: { invalid } }) => (
                <InsuranceLookup
                  {...field}
                  error={invalid}
                  id="insurance-name"
                  value={field?.value?.payerName ?? ''}
                  className="!mb-0"
                  placeholder="<Match Any>"
                />
              )}
            />
            <Controller
              control={control}
              name="serviceFacility"
              render={({ field }) => (
                <ServiceFacilitySelect
                  {...field}
                  label="Service Facility"
                  value={field?.value ?? undefined}
                  includeClaimsInfo
                  additionalOptions={[{
                    serviceFacilityId: 0,
                    nickname: '<Match Any>',
                    npi: {
                      number: '',
                    },
                  }]}
                />
              )}
            />
            <Controller
              control={control}
              name="renderingProvider"
              render={({ field }) => (
                <Select<RenderingProviderDTO>
                  {...field}
                  id="rendering-provider"
                  className="text-gray-500 text-xs"
                  label="Rendering Provider"
                  placeholder="Select"
                  options={renderingProvidersWithNotSet ?? []}
                  getLabel={UserLabel}
                  getValue={(option) => option?.renderingProviderId ?? ''}
                />
              )}
            />
            <Controller
              control={control}
              name="billingProvider"
              rules={{ required: 'Billing Provider is Required' }}
              render={({ field }) => (
                <Select<BillingProviderDTO>
                  {...field}
                  id="billing-provider"
                  className=" text-gray-500 text-xs min"
                  label="Billing Provider"
                  placeholder="Select"
                  options={billingProviders ?? []}
                  getLabel={BillingProviderLabel}
                  getValue={(option) => option?.billingProviderId ?? ''}
                  required
                />
              )}
            />
          </div>
          <div className="flex gap-2 justify-between">
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(isNew, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
